/** CRM header + Settings access, keyed by auth role and employee designation. */

export type AccessRoleId =
  | 'admin'
  | 'employee'
  | 'employee_shop'
  | 'employee_office'
  | 'staff'
  | 'designer'
  | 'architect'
  | 'engineer'
  | 'supplier'
  | 'cpa';

export interface RoleAccessEntry {
  id: AccessRoleId;
  label: string;
  color: string;
  bg: string;
  home: string;
  header: string[];
  people?: { label: string; items: string[] };
  avatar: string[];
  settings: string[];
  /** In-app bell (+ View all) — what this role actually sees after write + display filters. */
  notifications: string[];
  notes?: string[];
}

export interface DesignationAccessEntry {
  id: string;
  label: string;
  appliesTo: string;
  extras: string[];
}

const SCOPED_INTERNAL_HEADER = [
  'Projects — Project Hub, Operations, Purchasing (no Leads, Pipeline, or Analytics)',
  'People (limited)',
  'Feedback + notifications',
];

const SCOPED_INTERNAL_PEOPLE = {
  label: 'People menu',
  items: [
    'Team calendar',
    'Company handbook',
    'Certifications & Achievements',
    'Org chart',
    'Contractors (if 1099 field staff only)',
  ],
};

const SCOPED_INTERNAL_AVATAR = [
  'Notification Settings',
  'Contractors (if 1099)',
];

const SCOPED_INTERNAL_SETTINGS = [
  'My Account only (via avatar menu)',
  'Benefits, Travel, Hiring, and workspace Settings URLs redirect to account or home',
];

const NOTIF_BELL_AND_ALL = 'Bell and View all notifications';
const NOTIF_SALES_BROADCAST = 'New leads and inbound SMS';
const NOTIF_PORTAL_CLIENT =
  'Client portal: feedback, file uploads, questionnaire completed, 3D / concept / drawing approvals';
const NOTIF_INVOLVED_FULL =
  'When you are on the work: task assigned, comments, @mentions (including PDF / drawing pins), inspiration board, plan reviews, lead todos, OPS proposal review, purchase requisitions';
const NOTIF_INVOLVED_SCOPED =
  'When you are assigned or @mentioned: tasks, comments, and mentions';
const NOTIF_FILE_VERSIONS = 'New project file versions';
const NOTIF_NO_DEAL_WON =
  'Deal won, change orders, and design-fee totals only with COO / CFO / Office Manager or an executive briefing — role alone does not get signed amounts';
const NOTIF_CARD_DIGEST = 'Card expense digest if company-card charges are assigned to you';
const NOTIF_PARTNER_WORK =
  'When you are on the work: task assigned, comments, @mentions (including PDF / drawing pins), inspiration board, plan reviews, onboarding assigned to you';
const NOTIF_PARTNER_EXCLUDES =
  'Does not include new leads, SMS, deal won, hiring, billing, RFQ, or signed totals';

export const ROLE_ACCESS_MAP: RoleAccessEntry[] = [
  {
    id: 'admin',
    label: 'Admin',
    color: '#7C3AED',
    bg: '#F5F3FF',
    home: 'Logo opens Executive Suite',
    header: [
      'Leads',
      'Pipeline — Board, Proposals Summary, Won Deals',
      'Projects — Hub, Operations, CRM Map, Materials, Billing & Payables, Purchasing, Marketing Hub, External Portals, Vendors',
      'Analytics — Sales Activity, Finances, UTM Builder',
      'People (full)',
      'Agent Activity bell',
      'Feedback + notifications',
    ],
    people: {
      label: 'People menu',
      items: [
        'Team calendar',
        'Benefits & payroll',
        'Company handbook',
        'Travel',
        'Hiring',
        'Certifications & Achievements',
        'Org chart',
        'Contractors',
      ],
    },
    avatar: [
      'Notification Settings',
      'My Card Expenses',
      'Contractors',
      'Media library',
      'Tools',
      'Lifetime Goals',
      'Settings',
    ],
    settings: [
      'My Account (includes notification debug)',
      'Proposal Rates',
      'Client Experience',
      'Project Defaults',
      'Integrations, CRM, Tools, API & Agents',
      'Team (users + designer $/hr)',
      'Release Notes',
      'Certifications & Achievements catalog',
      'Feedback inbox',
      'Admin (People Hub benefits)',
    ],
    notifications: [
      NOTIF_BELL_AND_ALL,
      NOTIF_SALES_BROADCAST,
      NOTIF_PORTAL_CLIENT,
      NOTIF_INVOLVED_FULL,
      NOTIF_FILE_VERSIONS,
      'Hiring: job applications, resume comments, crew / 1099 onboard, contractor timesheets submitted',
      'Billing & payables (ready to bill, payment approved) and designer invoices ready to review',
      'Architect standup notes (every admin)',
      'Supplier RFQ uploaded when you are on the deal, or as the admin fallback',
      'Capital plan updates (CEO-admin seats)',
      NOTIF_CARD_DIGEST,
      NOTIF_NO_DEAL_WON,
    ],
    notes: [
      'Only role that can write team compensation (userCompensation).',
      'Can write Finances overhead, payroll, and finance settings.',
    ],
  },
  {
    id: 'employee',
    label: 'Employee — Full',
    color: '#2C3E2F',
    bg: '#F0F7F1',
    home: 'Opens Leads',
    header: [
      'Leads',
      'Pipeline — Board, Proposals Summary, Won Deals',
      'Projects — Hub, Operations, CRM Map, Materials, Purchasing, Marketing Hub, External Portals, Vendors',
      'Analytics — Sales Activity, UTM Builder (Finances only with a designation below)',
      'People (full employee set)',
      'Feedback + notifications',
    ],
    people: {
      label: 'People menu',
      items: [
        'Team calendar',
        'Benefits & payroll (own record; not the org-wide queue)',
        'Company handbook',
        'Travel',
        'Hiring',
        'Certifications & Achievements',
        'Org chart',
      ],
    },
    avatar: [
      'Notification Settings',
      'My Card Expenses',
      'Media library',
      'Tools',
      'Settings',
    ],
    settings: [
      'My Account',
      'Proposal Rates (company defaults — editable)',
      'Client Experience',
      'Project Defaults',
      'Integrations, CRM, Tools, API & Agents',
    ],
    notifications: [
      NOTIF_BELL_AND_ALL,
      NOTIF_SALES_BROADCAST,
      NOTIF_PORTAL_CLIENT,
      NOTIF_INVOLVED_FULL,
      NOTIF_FILE_VERSIONS,
      'Hiring: job applications and crew / 1099 onboard',
      'Architect standup if you are that project’s PM',
      'Supplier RFQ uploaded if you are assigned on the deal',
      NOTIF_CARD_DIGEST,
      'Not billing, payables, or designer-invoice review unless a designation adds them',
      NOTIF_NO_DEAL_WON,
    ],
    notes: [
      'W-2 tier — 1099 contractor toggle is disabled in Team edit.',
      'No Team, Release Notes, award catalog, Feedback inbox, or Admin settings.',
      'No Billing & Payables, Finances, or Agent Activity unless a designation adds them.',
    ],
  },
  {
    id: 'employee_shop',
    label: 'Employee — Shop',
    color: '#475569',
    bg: '#EEF2F6',
    home: 'Opens Project Hub',
    header: SCOPED_INTERNAL_HEADER,
    people: SCOPED_INTERNAL_PEOPLE,
    avatar: SCOPED_INTERNAL_AVATAR,
    settings: SCOPED_INTERNAL_SETTINGS,
    notifications: [
      NOTIF_BELL_AND_ALL,
      NOTIF_SALES_BROADCAST,
      NOTIF_PORTAL_CLIENT,
      NOTIF_INVOLVED_SCOPED,
      'Does not include deal won, hiring, billing, org-wide file versions, or PDF team broadcasts',
    ],
    notes: [
      'W-2 employee tier — cannot be marked 1099 while this role is selected.',
      'Same scoped CRM as Field staff; use for shop-floor W-2 hires.',
    ],
  },
  {
    id: 'employee_office',
    label: 'Employee — Office',
    color: '#475569',
    bg: '#E8EEF4',
    home: 'Opens Project Hub',
    header: SCOPED_INTERNAL_HEADER,
    people: {
      label: 'People menu',
      items: [
        'Team (directory — view; admin still owns invites/edits)',
        'Team calendar',
        'Company handbook',
        'Certifications & Achievements',
        'Org chart',
        'Contractors (if 1099 field staff only)',
      ],
    },
    avatar: SCOPED_INTERNAL_AVATAR,
    settings: SCOPED_INTERNAL_SETTINGS,
    notifications: [
      NOTIF_BELL_AND_ALL,
      NOTIF_SALES_BROADCAST,
      NOTIF_PORTAL_CLIENT,
      NOTIF_INVOLVED_SCOPED,
      '1099 ready for QuickBooks when a contractor enters the pool',
      'Crew / 1099 contractor onboard',
      'Does not include deal won, billing, org-wide file versions, or PDF team broadcasts unless Office Manager is also set',
    ],
    notes: [
      'W-2 employee tier — same scoped CRM as Employee — Shop, plus People → Team.',
      'Purchasing shows the company PO list (process issued POs; no DoA override or budget remaining cards).',
      'Gets in-app notice when a 1099 enters the pool (QuickBooks vendor setup).',
    ],
  },
  {
    id: 'staff',
    label: 'Field staff',
    color: '#64748B',
    bg: '#F1F5F9',
    home: 'Opens Project Hub',
    header: SCOPED_INTERNAL_HEADER,
    people: SCOPED_INTERNAL_PEOPLE,
    avatar: SCOPED_INTERNAL_AVATAR,
    settings: SCOPED_INTERNAL_SETTINGS,
    notifications: [
      NOTIF_BELL_AND_ALL,
      NOTIF_SALES_BROADCAST,
      NOTIF_PORTAL_CLIENT,
      'When you are on the work: tasks, comments, @mentions, PDF / drawing pins, inspiration board, plan reviews',
      NOTIF_FILE_VERSIONS,
      'Personal task reminders (bell)',
      'Does not include job applications, deal won, or billing unless a designation adds them',
    ],
    notes: [
      'Same scoped CRM as Employee — Shop / Office (W-2 only).',
      '1099 contractors use Field staff role + is1099Contractor — My Hours shell, not this menu.',
      'Legacy role slug — prefer Employee — Shop for new W-2 shop hires.',
    ],
  },
  {
    id: 'designer',
    label: 'Designer',
    color: '#0369A1',
    bg: '#F0F9FF',
    home: 'Opens Design Portal',
    header: [
      'Design Work',
      'Standup',
      'Project Hub',
      'Inspiration board',
      'Contractors (if 1099)',
      'Feedback + notifications',
    ],
    avatar: [
      'Profile',
      'Notification Settings',
      'Change Password',
    ],
    settings: ['Account only if they type /settings — no Proposal Rates.'],
    notifications: [
      NOTIF_BELL_AND_ALL,
      NOTIF_PARTNER_WORK,
      NOTIF_FILE_VERSIONS,
      NOTIF_PORTAL_CLIENT,
      NOTIF_PARTNER_EXCLUDES,
    ],
    notes: [
      'Internal LCTF designers. No proposals, client portal, or financials.',
    ],
  },
  {
    id: 'architect',
    label: 'Architect',
    color: '#7C2D12',
    bg: '#FFF7ED',
    home: 'Opens Architect Portal',
    header: [
      'Status',
      'Project Hub (Design groups, 2D / HTML / 3D / general files)',
      'Inspiration board',
      'Feedback + notifications',
    ],
    avatar: [
      'Profile',
      'Notification Settings',
      'Change Password',
    ],
    settings: ['No Settings. Not a CRM workspace user.'],
    notifications: [
      NOTIF_BELL_AND_ALL,
      NOTIF_PARTNER_WORK,
      NOTIF_PORTAL_CLIENT,
      'Not notified for org-wide project file versions',
      NOTIF_PARTNER_EXCLUDES,
    ],
    notes: [
      'Granted per project. No proposals, client portal, or financials. Staff preview via Projects → External Portals.',
    ],
  },
  {
    id: 'engineer',
    label: 'Engineer',
    color: '#B45309',
    bg: '#FFF7ED',
    home: 'Opens Design Portal (Engineer Work)',
    header: [
      'Engineer Work',
      'Project Hub',
      'Inspiration board',
      'Feedback + notifications',
    ],
    avatar: [
      'Profile',
      'Notification Settings',
      'Change Password',
    ],
    settings: ['Account only if they type /settings — no Proposal Rates.'],
    notifications: [
      NOTIF_BELL_AND_ALL,
      NOTIF_PARTNER_WORK,
      NOTIF_FILE_VERSIONS,
      NOTIF_PORTAL_CLIENT,
      NOTIF_PARTNER_EXCLUDES,
    ],
    notes: [
      'No proposals, client portal, or financials. Blocked from contractor timesheets.',
    ],
  },
  {
    id: 'supplier',
    label: 'Supplier',
    color: '#166534',
    bg: '#ECFDF5',
    home: 'Opens Vendor Portal',
    header: [
      'Vendor Portal',
      'Projects → Project Hub only',
    ],
    avatar: [
      'Vendor Portal',
      'Sign Out',
    ],
    settings: ['Settings is blocked. The route redirects home.'],
    notifications: [
      'No CRM notification bell',
      'Quote requests and awards go to the vendor portal / email, not the in-app inbox',
    ],
  },
  {
    id: 'cpa',
    label: 'CPA',
    color: '#0F766E',
    bg: '#F0FDFA',
    home: 'Opens Finances',
    header: ['Finances only (Forecast, OpEx, Payroll, Project P&L, Contractor Payables)'],
    avatar: [
      'Profile',
      'Account settings',
      'Notification Settings',
      'Change Password',
    ],
    settings: ['My Account only. Every other Settings section redirects away.'],
    notifications: [
      'No CRM notification bell',
      'Email when @mentioned on a capital plan (case, costs, recent discussion)',
      'Notification Settings in the avatar menu still exist; there is no inbox',
    ],
    notes: [
      'Finances is read-only — they cannot write overhead, payroll, or finance settings.',
      'Can read team compensation; cannot write it.',
    ],
  },
];

export const DESIGNATION_ACCESS_MAP: DesignationAccessEntry[] = [
  {
    id: 'executive_assistant',
    label: 'Executive assistant',
    appliesTo: 'Employee — Full (or admin)',
    extras: [
      'Analytics → Finances (full: Forecast, OpEx, Payroll, P&L, Contractor Payables)',
      'Projects → Billing & Payables',
      'Agent Activity bell',
      'Org-wide benefits and time-off approval',
      'Write Finances settings',
      'Categorize anyone’s card expenses',
      'Enter contractor time on behalf of others',
      'Office Manager includes these extras plus full CRM elevation for scoped roles',
      'Bell skips Project Hub, portal, PDF, files, RFQ, deal-won, new leads, and SMS unless someone @mentions you',
      'Still gets org-ops bells: billing processor, contractor invoices, designer invoices ready to review',
    ],
  },
  {
    id: 'cfo',
    label: 'CFO',
    appliesTo: 'Employee — Full (or admin)',
    extras: [
      'Full Finances hub',
      'Billing & Payables',
      'Purchasing leadership (all requisitions + policy)',
      'Categorize anyone’s card expenses',
      'Cannot approve time-off or open Agent Activity',
      'Deal won, change orders, design agreement signed, and design fee paid (includes signed totals)',
      'Designer invoices ready to review, billing & payables, and capital plan updates',
    ],
  },
  {
    id: 'coo',
    label: 'COO',
    appliesTo: 'Employee — Full (or admin), including COO executive briefing',
    extras: [
      'Full Finances hub',
      'Billing & Payables',
      'Purchasing leadership',
      'Cannot categorize others’ card expenses',
      'Deal won, change orders, design agreement signed, and design fee paid (includes signed totals)',
      'Architect standup reports, billing & payables, and capital plan updates',
      'Does not review designer invoices or the job POs those approvals issue — even with project_manager',
    ],
  },
  {
    id: 'office_manager',
    label: 'Office manager',
    appliesTo: 'Any W-2 employee tier (typically Employee — Office)',
    extras: [
      'Same CRM menus and routes as Employee — Full (Leads, Pipeline, Analytics, workspace Settings)',
      'All Executive Assistant extras (Finances, Billing & Payables, Agent Activity, time-off approval, card expenses on behalf, etc.)',
      'People → Hiring, Benefits, Travel',
      'Does not grant admin-only tools or CFO-only extras (e.g. purchasing policy edit, installment payment approval)',
      'Deal won, change orders, and design-fee totals (office managers are on the exec notify list)',
      'Billing processor bells and designer invoices ready to review',
    ],
  },
  {
    id: 'project_manager',
    label: 'Project manager',
    appliesTo: 'Employee — Full (or admin)',
    extras: [
      'Analytics → Finances lands on Project P&L only (not Forecast or Payroll)',
      'Can mark items Ready to Bill',
      'Designer invoices ready to review (not the COO, even if they are also a PM)',
      'Architect standup if you are that project’s PM — not deal-won totals',
    ],
  },
  {
    id: 'contractor_1099',
    label: '1099 contractor',
    appliesTo: 'Field staff or designer (not W-2 employee tiers)',
    extras: [
      'People + avatar → Contractors (own timesheets)',
      'Pay type is separate from role — W-2 Employee — Full / Shop / Office cannot be 1099',
      'Engineers are excluded',
      'No extra notification types — same bell as the underlying role',
    ],
  },
  {
    id: 'sales_rep',
    label: 'Sales rep',
    appliesTo: 'Any W-2 employee tier (Full, Office, Shop, Field)',
    extras: [
      'Home opens Leads',
      'Header — Leads; Pipeline (Board, Proposals Summary, Won Deals)',
      'Projects — CRM Map and Materials, plus Hub / Operations / Purchasing from the base role',
      'Analytics — Sales Activity only (not UTM Builder or Finances)',
      'Proposal builder, client portal, inspiration board, Change Orders, PDF take-off',
      'Deal values on Project Hub',
      'People — Travel and own Benefits (not Hiring or the org-wide queue)',
      'My Card Expenses',
      'Settings — My Account + Proposal Rates (not Integrations / API / CRM / Team admin)',
      'Does not grant Finances, Billing, Marketing Hub, Vendors admin, Agent Activity, or estimator file/RFQ tools',
    ],
  },
  {
    id: 'estimator',
    label: 'Estimator',
    appliesTo: 'Employee — Full (or admin)',
    extras: [
      'Separate from Sales Rep — does not unlock Leads, Pipeline, or take-off',
      'Estimator file work (reshape timber structures, RFQ amounts) stays on this designation when the base role already allows it',
    ],
  },
];

export const ACCESS_MAP_NOTES: { title: string; body: string }[] = [
  {
    title: 'Role vs pay type',
    body: 'Role controls CRM menus and Settings. The 1099 flag controls contractor timesheets and W-9 — not Leads or Pipeline. Employee — Full / Shop / Office are W-2 tiers; Team edit disables 1099 while those roles are selected. Settings → Team lists 1099 contractors in their own section (not Clients). New 1099 pool invites use Field staff role + is1099Contractor; Clients are homeowner / stray Google logins without CRM access.',
  },
  {
    title: 'Settings vs People',
    body: 'Settings → Certifications & Achievements is the admin award catalog. People → Certifications is where employees view and earn awards. Settings → Feedback is the admin inbox; the header Feedback button is how anyone except suppliers and CPAs submit a report.',
  },
  {
    title: 'Proposal rates vs personal rates',
    body: 'Settings → Proposal Rates are company-wide defaults (overhead, design, production, install, travel). Employee — Full and admins open full workspace Settings. Sales Rep on a scoped W-2 role gets My Account + Proposal Rates only. Personal designer $/hr lives on Settings → Team and in userCompensation — admin write, finance-forecast people read (admin, EA, CFO, COO, CPA).',
  },
  {
    title: 'Notification bell',
    body: 'The lists above are what each role sees in the CRM bell after write rules and display filters. Deal-won and design-fee bells include signed totals — only COO, CFO, Office Manager, org-chart executives, and executive-briefing seats get them. Executive assistants skip project, portal, and sales broadcasts unless @mentioned. Designers, architects, and engineers only see design-work bells. Release-note popups follow the publish audience and are not bell rows. #CRM Slack posts milestones independently of anyone’s notification settings.',
  },
  {
    title: 'Firestore',
    body: 'userCompensation is not on users/{uid}. Read requires Finances-forecast access. Write is CRM admin only. Hosting deploys do not ship this rule — deploy firestore.rules with the UI.',
  },
];
